import {
    isVoltageTelemetryInput,
    LEVEL_SOURCE_VALUES,
    normalizeLevelSource,
} from '../../panel/inventory/fuelConfigurator/fuelPolicyApiMapping';
import {
    convertLitersToPercentage,
    convertPercentageToLiters,
    equationCoeffsFromMapping,
} from '../../panel/inventory/fuelConfigurator/fuelMapEquation';

/**
 * Map raw fuel level to display percentage via fuel policy.
 * tableMap.mapping: { fuelLevelFrom, fuelLevelTo, mappedValue }
 * equationMap.mapping: { a2, a1, a0 } — calibration fit ℓ = a₂x² + a₁x + a₀ (x = %, ℓ = L):
 *   PERCENTAGE → forward (ℓ from x); LITRE → inverse (x from ℓ); VOLTAGE → ℓ = a₂V² + a₁V + a₀.
 */
function parseNumeric(value) {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function isPercentageTelemetryInput(telemetryInput) {
    return (telemetryInput || '').toUpperCase() === 'PERCENTAGE';
}

function isLitreTelemetryInput(telemetryInput) {
    return (telemetryInput || '').toUpperCase() === 'LITRE';
}

function hasValidDeviceLitres(fuelLevelLitre) {
    return parseNumeric(fuelLevelLitre) != null;
}

/** Live-card % for PERCENTAGE signal unit: show device fuelLevelPerc as-is. */
function resolveRawDisplayPercentage(fuelLevelPerc) {
    const rawPct = parseNumeric(fuelLevelPerc);
    return rawPct != null ? clamp(rawPct, 0, 100) : null;
}

function mapTableToLitres(rawVoltage, tableMap) {
    if (rawVoltage == null || Number.isNaN(rawVoltage) || !tableMap?.mapping?.length) {
        return null;
    }
    const rangeMapping = tableMap.mapping.find(
        (range) => rawVoltage >= range.fuelLevelFrom && rawVoltage <= range.fuelLevelTo,
    );
    return rangeMapping ? rangeMapping.mappedValue : null;
}

function evaluateFuelMapEquation(rawLevel, equationMap) {
    if (rawLevel == null || Number.isNaN(rawLevel) || !equationMap?.mapping) {
        return null;
    }
    const { a0, a1, a2 } = equationCoeffsFromMapping(equationMap);
    return convertPercentageToLiters(rawLevel, a0, a1, a2);
}

function mapLitresToPercentage(rawLitres, equationMap) {
    if (rawLitres == null || Number.isNaN(rawLitres) || !equationMap?.mapping) {
        return null;
    }
    const { a0, a1, a2 } = equationCoeffsFromMapping(equationMap);
    return convertLitersToPercentage(rawLitres, a0, a1, a2);
}

function isVoltageWithinRange(rawVoltage, fuelMappingPreference) {
    const range = fuelMappingPreference?.voltageRange;
    if (!range) return true;
    const vMin = parseNumeric(range.vMin ?? range.v_min);
    const vMax = parseNumeric(range.vMax ?? range.v_max);
    if (vMin == null || vMax == null) return true;
    return rawVoltage >= vMin && rawVoltage <= vMax;
}

function resolveVoltageToLitres(rawVoltage, fuelMappingPreference) {
    if (rawVoltage == null || Number.isNaN(rawVoltage) || !fuelMappingPreference) {
        return null;
    }
    if (!isVoltageWithinRange(rawVoltage, fuelMappingPreference)) {
        return null;
    }

    const { tableMap, equationMap, levelSource } = fuelMappingPreference;
    const useTable = levelSource === 'MAP TABLE'
        ? tableMap?.mapping?.length
        : (levelSource !== 'MAP EQUATION' && tableMap?.enabled && tableMap?.mapping?.length);
    const useEquation = levelSource === 'MAP EQUATION'
        ? equationMap?.mapping
        : (levelSource !== 'MAP TABLE' && equationMap?.enabled && equationMap?.mapping);

    if (useTable) {
        return mapTableToLitres(rawVoltage, tableMap);
    }
    if (useEquation) {
        return evaluateFuelMapEquation(rawVoltage, equationMap);
    }
    return null;
}

function litresToPercentage(litres, tankCapacity) {
    const tank = Number(tankCapacity);
    if (litres == null || Number.isNaN(litres) || !tank || tank <= 0) {
        return null;
    }
    return clamp((litres / tank) * 100, 0, 100);
}

function resolveVoltageToPercentage(rawVoltage, fuelMappingPreference) {
    const tankCapacity = Number(fuelMappingPreference?.tankCapacity);
    if (!tankCapacity || tankCapacity <= 0) {
        return null;
    }
    const litres = resolveVoltageToLitres(rawVoltage, fuelMappingPreference);
    if (litres == null || Number.isNaN(litres)) {
        return null;
    }
    const clampedLitres = clamp(litres, 0, tankCapacity);
    return litresToPercentage(clampedLitres, tankCapacity);
}
function mapTableOnly(rawFuelLevel, tableMap) {
    if (rawFuelLevel == null || Number.isNaN(rawFuelLevel) || !tableMap?.mapping?.length) {
        return null;
    }
    const rangeMapping = tableMap.mapping.find(
        (range) => rawFuelLevel >= range.fuelLevelFrom && rawFuelLevel <= range.fuelLevelTo,
    );
    return rangeMapping ? rangeMapping.mappedValue : null;
}

/** When tank capacity is unavailable: prefer raw device %, else legacy equation output as %. */
function resolveEquationPercentageWithoutTank(rawLevel, equationMap, fuelLevelPerc) {
    const rawPct = parseNumeric(fuelLevelPerc);
    if (rawPct != null) {
        return clamp(rawPct, 0, 100);
    }
    const mapped = evaluateFuelMapEquation(rawLevel, equationMap);
    if (mapped == null || Number.isNaN(mapped)) {
        return null;
    }
    return clamp(mapped, 0, 100);
}

function mappingUsesEquation(fuelMappingPreference) {
    const levelSource = normalizeLevelSource(fuelMappingPreference?.levelSource);
    return levelSource === 'MAP EQUATION'
        || (levelSource !== 'MAP TABLE'
            && fuelMappingPreference?.equationMap?.enabled
            && fuelMappingPreference?.equationMap?.mapping);
}

/** Prefer device % for forward equation only when LITRE policy has no litres telemetry. */
function shouldUsePercentageForEquation(fuelMappingPreference, fuelLevelPerc, fuelLevelLitre) {
    if (!mappingUsesEquation(fuelMappingPreference)) {
        return false;
    }
    if (isVoltageTelemetryInput(fuelMappingPreference?.telemetryInput)) {
        return false;
    }
    if (isLitreTelemetryInput(fuelMappingPreference.telemetryInput) && hasValidDeviceLitres(fuelLevelLitre)) {
        return false;
    }
    return parseNumeric(fuelLevelPerc) != null;
}

/** PERCENTAGE / BOTH: raw device %; LITRE: mapped % from inverse unless only % is available. */
function resolvePolicyDisplayPercentage(
    fuelMappingPreference,
    fuelLevelPerc,
    fuelLevelLitre,
    mappedPercentage,
) {
    const input = (fuelMappingPreference?.telemetryInput || 'BOTH').toUpperCase();
    if (isPercentageTelemetryInput(input) || input === 'BOTH') {
        const rawPct = resolveRawDisplayPercentage(fuelLevelPerc);
        if (rawPct != null) {
            return rawPct;
        }
    }
    if (isLitreTelemetryInput(input)
        && shouldUsePercentageForEquation(fuelMappingPreference, fuelLevelPerc, fuelLevelLitre)) {
        return resolveRawDisplayPercentage(fuelLevelPerc);
    }
    return mappedPercentage;
}

function usesPercentageEquationForLitres(fuelMappingPreference, fuelLevelPerc, fuelLevelLitre) {
    if (!mappingUsesEquation(fuelMappingPreference)) {
        return false;
    }
    if (isVoltageTelemetryInput(fuelMappingPreference?.telemetryInput)) {
        return false;
    }
    if (shouldUsePercentageForEquation(fuelMappingPreference, fuelLevelPerc, fuelLevelLitre)) {
        return true;
    }
    return !isLitresEquationInput(
        fuelMappingPreference.telemetryInput,
        fuelLevelPerc,
        fuelLevelLitre,
    );
}

function resolveEquationInputLevel(
    fuelLevelPerc,
    fuelLevelLitre,
    fuelMappingPreference,
    fuelLevelVoltage = null,
) {
    if (shouldUsePercentageForEquation(fuelMappingPreference, fuelLevelPerc, fuelLevelLitre)) {
        return parseNumeric(fuelLevelPerc);
    }
    return resolveRawLevelForLevelSource(
        fuelLevelPerc,
        fuelLevelLitre,
        fuelMappingPreference,
        fuelLevelVoltage,
    );
}

function resolveEquationLitresFromTelemetry(
    fuelLevelPerc,
    fuelLevelLitre,
    fuelMappingPreference,
    displayTankCapacity,
    fuelLevelVoltage = null,
) {
    if (!usesPercentageEquationForLitres(fuelMappingPreference, fuelLevelPerc, fuelLevelLitre)) {
        return null;
    }
    const equationLevel = resolveEquationInputLevel(
        fuelLevelPerc,
        fuelLevelLitre,
        fuelMappingPreference,
        fuelLevelVoltage,
    );
    if (equationLevel == null || Number.isNaN(equationLevel)) {
        return null;
    }
    return resolveEquationMappedLitres(
        equationLevel,
        fuelMappingPreference,
        fuelLevelPerc,
        fuelLevelLitre,
        displayTankCapacity,
    );
}

/** True when equation input is litres (LITRE, or BOTH with only litres). */
function isLitresEquationInput(telemetryInput, fuelLevelPerc, fuelLevelLitre) {
    const input = (telemetryInput || 'BOTH').toUpperCase();
    if (isVoltageTelemetryInput(input)) {
        return false;
    }
    if (input === 'LITRE') {
        return true;
    }
    if (input === 'PERCENTAGE') {
        return false;
    }
    const rawPct = fuelLevelPerc != null && fuelLevelPerc !== '' ? Number(fuelLevelPerc) : null;
    const pctValid = rawPct != null && !Number.isNaN(rawPct);
    if (pctValid) {
        return false;
    }
    if (fuelLevelLitre != null && fuelLevelLitre !== '') {
        const parsed = Number(fuelLevelLitre);
        return !Number.isNaN(parsed);
    }
    return false;
}

function resolveEquationTankCapacity(fuelMappingPreference) {
    const effective = Number(fuelMappingPreference?.tankCapacity);
    if (Number.isFinite(effective) && effective > 0) {
        return effective;
    }
    const policyTank = Number(fuelMappingPreference?.fuelPolicyTankCapacity);
    return Number.isFinite(policyTank) && policyTank > 0 ? policyTank : null;
}

function resolveEquationMappedPercentage(
    rawLevel,
    fuelMappingPreference,
    fuelLevelPerc = null,
    fuelLevelLitre = null,
) {
    if (rawLevel == null || Number.isNaN(rawLevel)) {
        return null;
    }

    if (isVoltageTelemetryInput(fuelMappingPreference?.telemetryInput)) {
        return resolveVoltageToPercentage(rawLevel, fuelMappingPreference);
    }

    const equationMap = fuelMappingPreference?.equationMap;
    if (!equationMap?.mapping) {
        return null;
    }

    const litresInput = isLitresEquationInput(
        fuelMappingPreference.telemetryInput,
        fuelLevelPerc,
        fuelLevelLitre,
    );

    if (litresInput) {
        const policyTank = resolveEquationTankCapacity(fuelMappingPreference);
        const linearPct = policyTank ? litresToPercentage(rawLevel, policyTank) : null;

        if (mappingUsesEquation(fuelMappingPreference)) {
            const mapped = mapLitresToPercentage(rawLevel, equationMap);
            if (mapped != null && !Number.isNaN(mapped)) {
                return clamp(mapped, 0, 100);
            }
        }

        return linearPct;
    }

    const policyTank = resolveEquationTankCapacity(fuelMappingPreference);
    const mappedLitres = evaluateFuelMapEquation(rawLevel, equationMap);
    if (mappedLitres == null || Number.isNaN(mappedLitres)) {
        return null;
    }

    if (!policyTank) {
        return resolveEquationPercentageWithoutTank(rawLevel, equationMap, fuelLevelPerc);
    }

    if (mappedLitres > policyTank) {
        const rawPct = parseNumeric(fuelLevelPerc);
        return rawPct != null ? clamp(rawPct, 0, 100) : 100;
    }

    return litresToPercentage(Math.max(0, mappedLitres), policyTank);
}

function resolveEquationMappedLitres(
    rawLevel,
    fuelMappingPreference,
    fuelLevelPerc,
    fuelLevelLitre,
    displayTankCapacity,
) {
    if (isVoltageTelemetryInput(fuelMappingPreference?.telemetryInput)) {
        const displayTank = Number(displayTankCapacity);
        if (!(displayTank > 0 && rawLevel != null && !Number.isNaN(rawLevel))) {
            return null;
        }
        const mappedLitres = resolveVoltageToLitres(rawLevel, fuelMappingPreference);
        if (mappedLitres == null || Number.isNaN(mappedLitres)) {
            return null;
        }
        return clamp(mappedLitres, 0, displayTank);
    }

    const policyTank = resolveEquationTankCapacity(fuelMappingPreference);
    const mappedLitres = evaluateFuelMapEquation(rawLevel, fuelMappingPreference?.equationMap);
    if (mappedLitres == null || Number.isNaN(mappedLitres)) {
        return null;
    }

    if (policyTank && mappedLitres > policyTank) {
        const rawPct = parseNumeric(fuelLevelPerc);
        const displayTank = Number(displayTankCapacity);
        if (rawPct != null && displayTank > 0) {
            return (rawPct / 100) * displayTank;
        }
        return clamp(mappedLitres, 0, policyTank);
    }

    if (policyTank) {
        return clamp(mappedLitres, 0, policyTank);
    }
    return Math.max(0, mappedLitres);
}

function isTankCapacityDetectionLevelSource(levelSource) {
    return normalizeLevelSource(levelSource) === LEVEL_SOURCE_VALUES.TANK_CAPACITY_DETECTION;
}

/** Vehicle preset fuelSetup.maxTankVolumeL when set by admin. */
export function resolveVehiclePresetTankCapacity(vehiclePreset) {
    const maxTank = vehiclePreset?.fuelSetup?.maxTankVolumeL;
    const parsed = Number(maxTank);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** Effective tank capacity: vehicle preset override → fuel policy → device (legacy). */
export function resolveEffectiveTankCapacity(vehiclePreset, policyTankCapacity, deviceFuelCapacity) {
    const presetTank = resolveVehiclePresetTankCapacity(vehiclePreset);
    if (presetTank) return presetTank;
    const policyTank = Number(policyTankCapacity);
    if (Number.isFinite(policyTank) && policyTank > 0) return policyTank;
    const deviceTank = Number(deviceFuelCapacity);
    if (Number.isFinite(deviceTank) && deviceTank > 0) return deviceTank;
    return null;
}

/** Normalize fuel policy API doc or legacy fuelMappingPreference into mapping input. */
export function toFuelMappingInput(fuelPolicy, { vehiclePreset, deviceFuelCapacity } = {}) {
    if (!fuelPolicy) {
        return null;
    }

    const policyTank = fuelPolicy.tankCapacity ?? fuelPolicy.tankCapacityL;
    const policyTankNum = Number(policyTank);
    const effectiveTank = resolveEffectiveTankCapacity(vehiclePreset, policyTank, deviceFuelCapacity);

    return {
        tankCapacity: effectiveTank,
        fuelPolicyTankCapacity: Number.isFinite(policyTankNum) && policyTankNum > 0
            ? policyTankNum
            : undefined,
        telemetryInput: fuelPolicy.telemetryInput || 'BOTH',
        levelSource: normalizeLevelSource(fuelPolicy.levelSource),
        tableMap: fuelPolicy.tableMap,
        equationMap: fuelPolicy.equationMap,
        percentageMapping: fuelPolicy.percentageMapping,
        voltageRange: fuelPolicy.voltageRange,
    };
}

/**
 * Linear % from tank capacity + telemetry (no table/equation).
 * Tank capacity comes from vehicle preset when admin overrides the linked fuel policy.
 */
function computePercentageFromPresetTankCapacity(
    fuelLevelPerc,
    fuelLevelLitre,
    fuelMappingPreference,
    tankCapacityL,
) {
    const tankCapacity = Number(tankCapacityL);
    if (!tankCapacity || tankCapacity <= 0) {
        return null;
    }

    const telemetryInput = fuelMappingPreference?.telemetryInput || 'BOTH';
    const rawPct = fuelLevelPerc != null && fuelLevelPerc !== '' ? Number(fuelLevelPerc) : null;
    const pctValid = rawPct != null && !Number.isNaN(rawPct);

    let litres = null;
    if (fuelLevelLitre != null && fuelLevelLitre !== '') {
        const parsed = Number(fuelLevelLitre);
        litres = Number.isNaN(parsed) ? null : parsed;
    }
    const litresValid = litres != null;

    if (telemetryInput === 'PERCENTAGE' && pctValid) {
        return rawPct;
    }
    if (telemetryInput === 'LITRE' && litresValid) {
        return (litres / tankCapacity) * 100;
    }
    if (telemetryInput === 'BOTH') {
        if (pctValid) {
            return rawPct;
        }
        if (litresValid) {
            return (litres / tankCapacity) * 100;
        }
    }

    return null;
}

function resolveLitresForDisplay(
    fuelLevelPerc,
    fuelLevelLitre,
    percentage,
    tankCapacityL,
) {
    if (fuelLevelLitre != null && fuelLevelLitre !== '') {
        const rawLitres = Number(fuelLevelLitre);
        return Number.isNaN(rawLitres) ? null : rawLitres;
    }
    const capacity = Number(tankCapacityL);
    if (percentage != null && !Number.isNaN(percentage) && capacity > 0) {
        return (percentage / 100) * capacity;
    }
    return null;
}

function mapRawFuelLevelToPercentage(
    rawFuelLevel,
    fuelMappingPreference,
    fuelLevelPerc = null,
    fuelLevelLitre = null,
) {
    if (rawFuelLevel == null || !fuelMappingPreference) {
        return null;
    }

    const { tableMap, equationMap, levelSource } = fuelMappingPreference;
    const useTable = levelSource === 'MAP TABLE'
        ? tableMap?.mapping?.length
        : (levelSource !== 'MAP EQUATION' && tableMap?.enabled && tableMap?.mapping?.length);
    const useEquation = levelSource === 'MAP EQUATION'
        ? equationMap?.mapping
        : (levelSource !== 'MAP TABLE' && equationMap?.enabled && equationMap?.mapping);

    if (useTable) {
        const rangeMapping = tableMap.mapping.find(
            (range) => rawFuelLevel >= range.fuelLevelFrom && rawFuelLevel <= range.fuelLevelTo,
        );
        if (rangeMapping) {
            return rangeMapping.mappedValue;
        }
        return null;
    }

    if (useEquation) {
        return resolveEquationMappedPercentage(
            rawFuelLevel,
            fuelMappingPreference,
            fuelLevelPerc,
            fuelLevelLitre,
        );
    }

    return null;
}

function resolveFuelStateFromPercentage(percentage, fuelMappingPreference) {
    let state = null;
    let isLow = false;

    if (percentage == null || !fuelMappingPreference?.percentageMapping) {
        if (percentage != null && percentage < 20) {
            isLow = true;
        }
        return { state, isLow };
    }

    const rangeMapping = fuelMappingPreference.percentageMapping.find(
        (range) => percentage <= range.percentageFrom && percentage >= range.percentageTo,
    );

    if (rangeMapping) {
        state = rangeMapping.state;
        const stateLower = rangeMapping.state?.toLowerCase() || '';
        isLow = stateLower.includes('refuel') || stateLower.includes('low');
    } else if (percentage < 20) {
        isLow = true;
    }

    return { state, isLow };
}

function resolveMappedPercentageForLevelSource(
    rawFuelLevel,
    fuelMappingPreference,
    fuelLevelPerc = null,
    fuelLevelLitre = null,
) {
    if (rawFuelLevel == null || !fuelMappingPreference) {
        return null;
    }

    if (isVoltageTelemetryInput(fuelMappingPreference.telemetryInput)) {
        return resolveVoltageToPercentage(rawFuelLevel, fuelMappingPreference);
    }

    const { levelSource } = fuelMappingPreference;
    if (isTankCapacityDetectionLevelSource(levelSource)) {
        return null;
    }
    if (levelSource === 'MAP TABLE') {
        return mapTableOnly(rawFuelLevel, fuelMappingPreference.tableMap);
    }
    if (levelSource === 'MAP EQUATION') {
        return resolveEquationMappedPercentage(
            rawFuelLevel,
            fuelMappingPreference,
            fuelLevelPerc,
            fuelLevelLitre,
        );
    }

    return mapRawFuelLevelToPercentage(
        rawFuelLevel,
        fuelMappingPreference,
        fuelLevelPerc,
        fuelLevelLitre,
    );
}

/**
 * Raw level fed into table/equation mapping.
 * Controlled by policy `telemetryInput`:
 *   LITRE       → fuelLevelLitre (litres)
 *   PERCENTAGE  → fuelLevelPerc (% value as-is, e.g. 33)
 *   BOTH        → fuelLevelPerc if present, else fuelLevelLitre
 *   VOLTAGE     → fuelLevelVoltage (normalised volts)
 */
function resolveRawLevelForLevelSource(
    fuelLevelPerc,
    fuelLevelLitre,
    fuelMappingPreference,
    fuelLevelVoltage = null,
) {
    const telemetryInput = fuelMappingPreference?.telemetryInput || 'BOTH';

    if (isVoltageTelemetryInput(telemetryInput)) {
        return parseNumeric(fuelLevelVoltage);
    }

    const rawPct = fuelLevelPerc != null && fuelLevelPerc !== '' ? Number(fuelLevelPerc) : null;
    const pctValid = rawPct != null && !Number.isNaN(rawPct);

    let litres = null;
    if (fuelLevelLitre != null && fuelLevelLitre !== '') {
        const parsed = Number(fuelLevelLitre);
        litres = Number.isNaN(parsed) ? null : parsed;
    }
    const litresValid = litres != null;

    if (telemetryInput === 'LITRE') {
        return litresValid ? litres : null;
    }
    if (telemetryInput === 'PERCENTAGE') {
        return pctValid ? rawPct : null;
    }
    // BOTH — prefer percentage when the device sends it
    if (pctValid) {
        return rawPct;
    }
    if (litresValid) {
        return litres;
    }

    return null;
}

/** Fuel state label from vehicle preset fuel policy + active levelSource. */
export function resolveLevelSourceFuelState(
    fuelLevelPerc,
    fuelLevelLitre,
    fuelPolicy,
    deviceFuelCapacity,
    vehiclePreset,
    fuelLevelVoltage = null,
) {
    const mapping = toFuelMappingInput(fuelPolicy, { vehiclePreset, deviceFuelCapacity });
    if (!mapping?.levelSource) {
        return null;
    }

    if (isPercentageTelemetryInput(mapping.telemetryInput)) {
        const rawDisplay = resolveRawDisplayPercentage(fuelLevelPerc);
        if (rawDisplay != null) {
            return resolveFuelStateFromPercentage(rawDisplay, mapping).state;
        }
    }

    if (isTankCapacityDetectionLevelSource(mapping.levelSource)) {
        const percentage = computePercentageFromPresetTankCapacity(
            fuelLevelPerc,
            fuelLevelLitre,
            mapping,
            mapping.tankCapacity,
        );
        if (percentage == null || Number.isNaN(percentage)) {
            return null;
        }
        return resolveFuelStateFromPercentage(percentage, mapping).state;
    }

    const rawLevel = resolveRawLevelForLevelSource(
        fuelLevelPerc,
        fuelLevelLitre,
        mapping,
        fuelLevelVoltage,
    );
    if (rawLevel == null) {
        return null;
    }

    const mappedPercentage = resolveMappedPercentageForLevelSource(
        rawLevel,
        mapping,
        fuelLevelPerc,
        fuelLevelLitre,
    );
    if (mappedPercentage == null || Number.isNaN(mappedPercentage)) {
        return null;
    }

    return resolveFuelStateFromPercentage(mappedPercentage, mapping).state;
}

/**
 * Resolve fuel % and litres for asset cards.
 * Priority: fuel policy levelSource (MAP TABLE / MAP EQUATION) → direct litres → legacy (pct × capacity).
 */
export function computeFuelLevels(
    fuelLevelPerc,
    fuelLevelLitre,
    fuelMappingPreference,
    deviceFuelCapacity,
    vehiclePreset,
    fuelLevelVoltage = null,
) {
    const fuelPolicyTank = fuelMappingPreference?.fuelPolicyTankCapacity
        ?? fuelMappingPreference?.tankCapacity
        ?? fuelMappingPreference?.tankCapacityL;
    const effectiveTank = resolveEffectiveTankCapacity(
        vehiclePreset,
        fuelPolicyTank,
        deviceFuelCapacity,
    );
    const mapping = effectiveTank
        ? { ...fuelMappingPreference, tankCapacity: effectiveTank }
        : fuelMappingPreference;
    const tankForMapping = effectiveTank;
    const displayCapacity = effectiveTank;

    if (mapping?.levelSource && isTankCapacityDetectionLevelSource(mapping.levelSource)) {
        const percentage = computePercentageFromPresetTankCapacity(
            fuelLevelPerc,
            fuelLevelLitre,
            mapping,
            effectiveTank,
        );
        if (percentage == null || Number.isNaN(percentage)) {
            return {
                litres: null,
                percentage: null,
                state: null,
                isLow: false,
            };
        }
        const { state, isLow } = resolveFuelStateFromPercentage(percentage, mapping);
        return {
            litres: resolveLitresForDisplay(
                fuelLevelPerc,
                fuelLevelLitre,
                percentage,
                effectiveTank,
            ),
            percentage,
            state,
            isLow,
        };
    }

    const pctEquationFallback = shouldUsePercentageForEquation(mapping, fuelLevelPerc, fuelLevelLitre);

    if (mapping?.levelSource) {
        const rawLevel = resolveRawLevelForLevelSource(
            fuelLevelPerc,
            fuelLevelLitre,
            mapping,
            fuelLevelVoltage,
        );
        if (rawLevel != null || pctEquationFallback) {
            const equationInputLevel = resolveEquationInputLevel(
                fuelLevelPerc,
                fuelLevelLitre,
                mapping,
                fuelLevelVoltage,
            );
            const mappedPercentage = equationInputLevel != null
                ? resolveMappedPercentageForLevelSource(
                    equationInputLevel,
                    mapping,
                    fuelLevelPerc,
                    fuelLevelLitre,
                )
                : null;
            const displayPercentage = resolvePolicyDisplayPercentage(
                mapping,
                fuelLevelPerc,
                fuelLevelLitre,
                mappedPercentage,
            );
            if (displayPercentage != null && !Number.isNaN(displayPercentage)) {
                let litres = null;

                if (isVoltageTelemetryInput(mapping.telemetryInput)) {
                    const tank = effectiveTank;
                    const mappedLitres = resolveVoltageToLitres(rawLevel, mapping);
                    litres = mappedLitres != null && Number.isFinite(mappedLitres) && tank > 0
                        ? clamp(mappedLitres, 0, tank)
                        : null;
                } else if (isLitreTelemetryInput(mapping.telemetryInput) && hasValidDeviceLitres(fuelLevelLitre)) {
                    litres = parseNumeric(fuelLevelLitre);
                } else {
                    litres = resolveEquationLitresFromTelemetry(
                        fuelLevelPerc,
                        fuelLevelLitre,
                        mapping,
                        displayCapacity,
                        fuelLevelVoltage,
                    );
                }

                if (litres == null && hasValidDeviceLitres(fuelLevelLitre)) {
                    litres = parseNumeric(fuelLevelLitre);
                } else if (litres == null && mappedPercentage != null && displayCapacity > 0) {
                    litres = (mappedPercentage / 100) * displayCapacity;
                }
                const { state, isLow } = resolveFuelStateFromPercentage(
                    displayPercentage,
                    mapping,
                );
                return {
                    litres,
                    percentage: displayPercentage,
                    state,
                    isLow,
                };
            }
        }
    }

    // Direct litres (LLS, BLE, external sensor) — use as-is; derive % from device capacity
    if (fuelLevelLitre != null && fuelLevelLitre !== '') {
        const litres = Number(fuelLevelLitre);
        const percentage = displayCapacity > 0 ? (litres / displayCapacity) * 100 : null;
        const { state, isLow } = resolveFuelStateFromPercentage(percentage, mapping);
        return {
            litres: Number.isNaN(litres) ? null : litres,
            percentage: percentage != null && !Number.isNaN(percentage) ? percentage : null,
            state,
            isLow,
        };
    }

    const rawPct = fuelLevelPerc != null && fuelLevelPerc !== '' ? Number(fuelLevelPerc) : null;

    if (rawPct != null && !Number.isNaN(rawPct) && mapping) {
        const telemetryInput = mapping.telemetryInput || 'BOTH';
        const litresInput = isLitresEquationInput(telemetryInput, fuelLevelPerc, fuelLevelLitre);
        const equationInputLevel = resolveEquationInputLevel(
            fuelLevelPerc,
            fuelLevelLitre,
            mapping,
            fuelLevelVoltage,
        );
        const rawLevelForMapping = equationInputLevel != null
            ? equationInputLevel
            : (litresInput && tankForMapping > 0
                ? (rawPct / 100) * tankForMapping
                : rawPct);
        const mappedPercentage = mapRawFuelLevelToPercentage(
            rawLevelForMapping,
            mapping,
            fuelLevelPerc,
            fuelLevelLitre,
        );

        if (mappedPercentage != null && !Number.isNaN(mappedPercentage)) {
            let litres = resolveEquationLitresFromTelemetry(
                fuelLevelPerc,
                fuelLevelLitre,
                mapping,
                displayCapacity,
                fuelLevelVoltage,
            );
            if (litres == null && displayCapacity > 0) {
                litres = (mappedPercentage / 100) * displayCapacity;
            }
            const displayPercentage = isPercentageTelemetryInput(telemetryInput)
                ? clamp(rawPct, 0, 100)
                : mappedPercentage;
            const { state, isLow } = resolveFuelStateFromPercentage(displayPercentage, mapping);
            return {
                litres,
                percentage: displayPercentage,
                state,
                isLow,
            };
        }
    }

    // Legacy: no policy mapping — linear % × capacity
    const percentage = rawPct;
    let litres = null;

    if (percentage != null && !Number.isNaN(percentage) && displayCapacity > 0) {
        litres = (percentage / 100) * displayCapacity;
    }

    if (percentage == null || Number.isNaN(percentage)) {
        return {
            litres: null,
            percentage: null,
            state: null,
            isLow: false,
        };
    }

    const { state, isLow } = resolveFuelStateFromPercentage(percentage, mapping);
    return {
        litres,
        percentage,
        state,
        isLow,
    };
}

/** Active fuel display values plus table/equation breakdown for asset-card detail. */
export function computeFuelMappingBreakdown(
    fuelLevelPerc,
    fuelLevelLitre,
    fuelPolicy,
    deviceFuelCapacity,
    vehiclePreset,
    fuelLevelVoltage = null,
) {
    const mapping = toFuelMappingInput(fuelPolicy, { vehiclePreset, deviceFuelCapacity });
    const active = computeFuelLevels(
        fuelLevelPerc,
        fuelLevelLitre,
        mapping,
        deviceFuelCapacity,
        vehiclePreset,
        fuelLevelVoltage,
    );

    const levelSourceState = resolveLevelSourceFuelState(
        fuelLevelPerc,
        fuelLevelLitre,
        fuelPolicy,
        deviceFuelCapacity,
        vehiclePreset,
        fuelLevelVoltage,
    );

    if (!mapping) {
        return {
            ...active,
            levelSourceState,
            tableMap: null,
            equationMap: null,
        };
    }

    if (isTankCapacityDetectionLevelSource(mapping.levelSource)) {
        return {
            ...active,
            levelSourceState,
            tableMap: null,
            equationMap: null,
        };
    }

    const rawLevelForMapping = resolveEquationInputLevel(
        fuelLevelPerc,
        fuelLevelLitre,
        mapping,
        fuelLevelVoltage,
    );

    if (rawLevelForMapping == null) {
        return {
            ...active,
            levelSourceState,
            tableMap: null,
            equationMap: null,
        };
    }

    if (isVoltageTelemetryInput(mapping.telemetryInput)) {
        const tablePct = resolveVoltageToPercentage(rawLevelForMapping, {
            ...mapping,
            levelSource: 'MAP TABLE',
            tableMap: { ...mapping.tableMap, enabled: true },
        });
        const equationPct = resolveVoltageToPercentage(rawLevelForMapping, {
            ...mapping,
            levelSource: 'MAP EQUATION',
            equationMap: { ...mapping.equationMap, enabled: true },
        });
        const tableState = resolveFuelStateFromPercentage(tablePct, mapping);
        const equationState = resolveFuelStateFromPercentage(equationPct, mapping);

        return {
            ...active,
            levelSourceState,
            tableMap: tablePct != null && !Number.isNaN(tablePct)
                ? { percentage: tablePct, state: tableState.state }
                : null,
            equationMap: equationPct != null && !Number.isNaN(equationPct)
                ? { percentage: equationPct, state: equationState.state }
                : null,
        };
    }

    const tablePct = mapTableOnly(rawLevelForMapping, mapping.tableMap);
    const equationPct = resolveEquationMappedPercentage(
        rawLevelForMapping,
        mapping,
        fuelLevelPerc,
        fuelLevelLitre,
    );
    const tableState = resolveFuelStateFromPercentage(tablePct, mapping);
    const equationState = resolveFuelStateFromPercentage(equationPct, mapping);

    return {
        ...active,
        levelSourceState,
        tableMap: tablePct != null && !Number.isNaN(tablePct)
            ? { percentage: tablePct, state: tableState.state }
            : null,
        equationMap: equationPct != null && !Number.isNaN(equationPct)
            ? { percentage: equationPct, state: equationState.state }
            : null,
    };
}
